export const M9_READING_PRACTICE = {
  id: "m9-reading-practice",
  title: "📚 Reading Practice - Module 9",
  topic: "Comprehensive reading practice integrating all grammar and vocabulary from Module 9. Real-world texts with advanced comprehension questions!",
  iconType: "book-open",
  
  sets: [
    {
      id: "m9-rp-s1",
      title: "Set 1: Health & Medical Innovation 🏥",
      context: `**PASSAGE A**

Dr. Sarah Chen, who has researched medical AI for fifteen years, leads a team at Stanford Medical Center. Her AI system, which analyzes thousands of medical images daily, has a 95% accuracy rate in detecting diseases early.

**PASSAGE B**

"If we implement these technologies correctly, we'll transform patient care," Dr. Chen explains. However, she warns that doctors must not rely completely on technology. "When doctors use AI tools, they make better diagnoses, but human expertise remains essential."

**PASSAGE C**

The technology costs millions to develop. "Unless funding increases, we can't make it accessible to all hospitals," Dr. Chen says. Her lab, which recently received a major grant, is now hiring more scientists to expand the research.

**PASSAGE D**

Maria Rodriguez was one of the first patients to benefit from the AI system. "The doctors who used this technology detected my rare condition early," she says. "If they hadn't found it when they did, I might not be here today."`,
      
      questions: [
        {
          type: "mc",
          q: "PASSAGE A: How long has Dr. Chen researched medical AI?",
          options: [
            "Fifteen years",
            "Five years",
            "Ninety-five years"
          ],
          a: "Fifteen years",
          tip: "Check the first sentence!",
          successtip: "✓ TIME PERIOD! Found the exact duration! 📅"
        },
        {
          type: "mc",
          q: "PASSAGE A: What is the AI system's accuracy rate?",
          options: [
            "95%",
            "15%",
            "100%"
          ],
          a: "95%",
          successtip: "✓ STATISTIC! Captured the percentage! 📊"
        },
        {
          type: "mc",
          q: "PASSAGE A: The clause 'who has researched medical AI' is:",
          options: [
            "Non-defining (extra info with commas)",
            "Defining (essential info, no commas)",
            "A question tag"
          ],
          a: "Non-defining (extra info with commas)",
          successtip: "✓ GRAMMAR! Identified non-defining relative clause! 📚"
        },
        {
          type: "mc",
          q: "PASSAGE B: 'If we implement these technologies correctly, we'll transform patient care' is:",
          options: [
            "First conditional (real future possibility)",
            "Second conditional (hypothetical)",
            "Zero conditional (general truth)"
          ],
          a: "First conditional (real future possibility)",
          successtip: "✓ CONDITIONAL! Recognized real possibility! 💡"
        },
        {
          type: "mc",
          q: "PASSAGE B: What must doctors NOT do?",
          options: [
            "Rely completely on technology",
            "Use AI tools",
            "Make diagnoses"
          ],
          a: "Rely completely on technology",
          tip: "Must not = prohibition!",
          successtip: "✓ PROHIBITION! Found what's not allowed! ⚠️"
        },
        {
          type: "mc",
          q: "PASSAGE B: When doctors use AI tools, they:",
          options: [
            "Make better diagnoses",
            "Make worse diagnoses",
            "Stop working"
          ],
          a: "Make better diagnoses",
          successtip: "✓ RESULT! Identified the outcome! ✅"
        },
        {
          type: "mc",
          q: "PASSAGE C: 'Unless funding increases' means:",
          options: [
            "If funding doesn't increase",
            "When funding increases",
            "Because funding increases"
          ],
          a: "If funding doesn't increase",
          tip: "Unless = if not!",
          successtip: "✓ UNLESS! Converted to if...not! Perfect! 🎯"
        },
        {
          type: "mc",
          q: "PASSAGE C: What did the lab recently receive?",
          options: [
            "A major grant",
            "More patients",
            "New hospitals"
          ],
          a: "A major grant",
          successtip: "✓ FUNDING! Found financial support! 💰"
        },
        {
          type: "mc",
          q: "PASSAGE C: The clause 'which recently received a major grant' is:",
          options: [
            "Non-defining (adds extra information)",
            "Defining (identifies which lab)",
            "Neither"
          ],
          a: "Non-defining (adds extra information)",
          successtip: "✓ CLAUSE TYPE! Recognized bonus information! 📌"
        },
        {
          type: "mc",
          q: "PASSAGE D: What condition did Maria have?",
          options: [
            "A rare condition",
            "A common cold",
            "No condition"
          ],
          a: "A rare condition",
          successtip: "✓ MEDICAL INFO! Identified the diagnosis! 🏥"
        },
        {
          type: "mc",
          q: "PASSAGE D: 'If they hadn't found it when they did' suggests:",
          options: [
            "She's grateful they found it in time",
            "They didn't find it",
            "It wasn't important"
          ],
          a: "She's grateful they found it in time",
          tip: "Past hypothetical shows relief!",
          successtip: "✓ EMOTION! Understood gratitude expression! 💭"
        },
        {
          type: "mc",
          q: "PASSAGE D: The clause 'who used this technology' describes:",
          options: [
            "Which doctors (the ones using AI)",
            "All doctors in general",
            "Maria Rodriguez"
          ],
          a: "Which doctors (the ones using AI)",
          successtip: "✓ DEFINING! Specifies which doctors! 👨‍⚕️"
        }
      ]
    },

    {
      id: "m9-rp-s2",
      title: "Set 2: Climate Science & Action 🌍",
      context: `**PASSAGE A**

When you heat the atmosphere by trapping greenhouse gases, global temperatures rise. This scientific fact has been proven repeatedly. Dr. James Wilson, who leads the Climate Research Institute, warns that if we don't reduce emissions soon, we'll face catastrophic consequences.

**PASSAGE B**

The institute was founded in 1985 and studies climate patterns worldwide. Their latest report, which was published last month, contains alarming data. The research shows that when ice melts in the Arctic, sea levels rise significantly.

**PASSAGE C**

Scientists who work on climate research must analyze massive amounts of data. First, data is collected from weather stations. Then, it's analyzed using supercomputers. Finally, results are reviewed by independent experts who verify the findings.

**PASSAGE D**

Solutions exist if governments act now. Sweden, which produces 50% of its energy from renewable sources, has cut emissions by 30%. "Unless we act immediately, the window for action will close," Dr. Wilson warns.`,
      
      questions: [
        {
          type: "mc",
          q: "PASSAGE A: 'When you heat the atmosphere, global temperatures rise' is:",
          options: [
            "Zero conditional (scientific fact)",
            "First conditional (future possibility)",
            "Second conditional (hypothetical)"
          ],
          a: "Zero conditional (scientific fact)",
          tip: "Present + present = general truth!",
          successtip: "✓ ZERO CONDITIONAL! Scientific fact pattern! 🔬"
        },
        {
          type: "mc",
          q: "PASSAGE A: What does Dr. Wilson lead?",
          options: [
            "The Climate Research Institute",
            "A university",
            "The government"
          ],
          a: "The Climate Research Institute",
          successtip: "✓ POSITION! Identified his role! 👨‍🔬"
        },
        {
          type: "mc",
          q: "PASSAGE A: 'If we don't reduce emissions soon' is:",
          options: [
            "First conditional (warning about real future)",
            "Zero conditional (scientific fact)",
            "Second conditional (impossible situation)"
          ],
          a: "First conditional (warning about real future)",
          successtip: "✓ CONDITIONAL! Recognized warning! ⚠️"
        },
        {
          type: "mc",
          q: "PASSAGE B: When was the institute founded?",
          options: [
            "1985",
            "Last month",
            "50 years ago"
          ],
          a: "1985",
          successtip: "✓ DATE! Found founding year! 📅"
        },
        {
          type: "mc",
          q: "PASSAGE B: When was the latest report published?",
          options: [
            "Last month",
            "In 1985",
            "In the Arctic"
          ],
          a: "Last month",
          successtip: "✓ RECENT EVENT! Publication timing! 📊"
        },
        {
          type: "mc",
          q: "PASSAGE B: 'When ice melts in the Arctic, sea levels rise' is:",
          options: [
            "Zero conditional (cause and effect)",
            "First conditional (future prediction)",
            "Second conditional (imaginary)"
          ],
          a: "Zero conditional (cause and effect)",
          successtip: "✓ SCIENTIFIC TRUTH! Natural consequence! 🌊"
        },
        {
          type: "mc",
          q: "PASSAGE C: What happens FIRST in the research process?",
          options: [
            "Data is collected from weather stations",
            "Results are reviewed",
            "Data is analyzed"
          ],
          a: "Data is collected from weather stations",
          tip: "First step!",
          successtip: "✓ SEQUENCE! Identified first step! 1️⃣"
        },
        {
          type: "mc",
          q: "PASSAGE C: What analyzes the data?",
          options: [
            "Supercomputers",
            "Weather stations",
            "Independent experts"
          ],
          a: "Supercomputers",
          successtip: "✓ PROCESS! Found analysis method! 💻"
        },
        {
          type: "mc",
          q: "PASSAGE C: 'Scientists who work on climate research' - this clause is:",
          options: [
            "Defining (which scientists)",
            "Non-defining (extra info)",
            "A question"
          ],
          a: "Defining (which scientists)",
          successtip: "✓ DEFINING! Specifies which scientists! 🔬"
        },
        {
          type: "mc",
          q: "PASSAGE D: What percentage of Sweden's energy is renewable?",
          options: [
            "50%",
            "30%",
            "100%"
          ],
          a: "50%",
          successtip: "✓ ENERGY DATA! Renewable percentage! 🔋"
        },
        {
          type: "mc",
          q: "PASSAGE D: By how much has Sweden cut emissions?",
          options: [
            "30%",
            "50%",
            "15%"
          ],
          a: "30%",
          tip: "Different from renewable energy %!",
          successtip: "✓ REDUCTION! Emission decrease! 📉"
        },
        {
          type: "mc",
          q: "PASSAGE D: 'Unless we act immediately' means:",
          options: [
            "If we don't act immediately",
            "When we act immediately",
            "Because we act"
          ],
          a: "If we don't act immediately",
          successtip: "✓ UNLESS! Converted correctly! ⚡"
        }
      ]
    },

    {
      id: "m9-rp-s3",
      title: "Set 3: Modern Workplace Trends 💼",
      context: `**PASSAGE A**

The pandemic forced millions to work from home, transforming workplace culture permanently. CEO Janet Martinez, who leads a tech company with 2,000 employees, says, "If we had known earlier how well remote work functions, we would have implemented it sooner."

**PASSAGE B**

Martinez's company operates with a hybrid model. Employees who want to work remotely three days a week can do so. "When workers have flexibility, productivity increases," she explains. Employee satisfaction scores have risen by 40% since implementing this policy.

**PASSAGE C**

However, challenges exist. "We must not lose the collaborative spirit," Martinez warns. The company has to organize regular team-building events. "Unless we maintain personal connections, remote work could become isolating," she notes.

**PASSAGE D**

Career coach Michael Torres advises professionals to develop timeless skills: communication, creativity, and critical thinking. His twelve-week coaching program costs $5,000 and has helped over 10,000 people transition to new careers successfully.`,
      
      questions: [
        {
          type: "mc",
          q: "PASSAGE A: What forced companies to adopt remote work?",
          options: [
            "The pandemic",
            "Employee requests",
            "New technology"
          ],
          a: "The pandemic",
          successtip: "✓ CAUSE! Identified trigger event! 🦠"
        },
        {
          type: "mc",
          q: "PASSAGE A: How many employees does Martinez's company have?",
          options: [
            "2,000",
            "40",
            "10,000"
          ],
          a: "2,000",
          successtip: "✓ COMPANY SIZE! Employee count! 👥"
        },
        {
          type: "mc",
          q: "PASSAGE A: 'If we had known earlier' is:",
          options: [
            "Third conditional (past hypothetical)",
            "First conditional (future possibility)",
            "Zero conditional (general truth)"
          ],
          a: "Third conditional (past hypothetical)",
          tip: "Had known = past unreal!",
          successtip: "✓ PAST HYPOTHETICAL! Regret/reflection! 💭"
        },
        {
          type: "mc",
          q: "PASSAGE B: How many days can employees work remotely?",
          options: [
            "Three days a week",
            "Every day",
            "Never"
          ],
          a: "Three days a week",
          successtip: "✓ POLICY! Work schedule! 📅"
        },
        {
          type: "mc",
          q: "PASSAGE B: By how much have satisfaction scores risen?",
          options: [
            "40%",
            "2,000%",
            "Three percent"
          ],
          a: "40%",
          successtip: "✓ METRIC! Satisfaction increase! 📈"
        },
        {
          type: "mc",
          q: "PASSAGE B: 'When workers have flexibility, productivity increases' is:",
          options: [
            "Zero conditional (general observation)",
            "First conditional (future prediction)",
            "Second conditional (hypothetical)"
          ],
          a: "Zero conditional (general observation)",
          successtip: "✓ PATTERN! Cause-effect truth! 🔄"
        },
        {
          type: "mc",
          q: "PASSAGE C: What must the company NOT lose?",
          options: [
            "The collaborative spirit",
            "Employees",
            "Technology"
          ],
          a: "The collaborative spirit",
          tip: "Must not = prohibition!",
          successtip: "✓ OBLIGATION! What to preserve! 🤝"
        },
        {
          type: "mc",
          q: "PASSAGE C: What does the company have to organize?",
          options: [
            "Regular team-building events",
            "Remote work",
            "Pandemics"
          ],
          a: "Regular team-building events",
          successtip: "✓ REQUIREMENT! Company obligation! 🎯"
        },
        {
          type: "mc",
          q: "PASSAGE C: 'Unless we maintain personal connections' means:",
          options: [
            "If we don't maintain personal connections",
            "When we maintain connections",
            "Because we maintain connections"
          ],
          a: "If we don't maintain personal connections",
          successtip: "✓ UNLESS! Perfect conversion! ✅"
        },
        {
          type: "mc",
          q: "PASSAGE D: What three timeless skills does Torres emphasize?",
          options: [
            "Communication, creativity, and critical thinking",
            "Remote work, technology, and isolation",
            "Only technical skills"
          ],
          a: "Communication, creativity, and critical thinking",
          successtip: "✓ KEY SKILLS! Essential abilities! 🎯"
        },
        {
          type: "mc",
          q: "PASSAGE D: How long is the coaching program?",
          options: [
            "Twelve weeks",
            "$5,000",
            "10,000 hours"
          ],
          a: "Twelve weeks",
          tip: "Duration, not cost!",
          successtip: "✓ DURATION! Program length! ⏰"
        },
        {
          type: "mc",
          q: "PASSAGE D: How many people has the program helped?",
          options: [
            "Over 10,000",
            "2,000",
            "$5,000"
          ],
          a: "Over 10,000",
          successtip: "✓ IMPACT! Total participants! 🌟"
        }
      ]
    },

    {
      id: "m9-rp-s4",
      title: "Set 4: Entrepreneurship Success Stories 🚀",
      context: `**PASSAGE A**

Lisa Chen started her company, TechSphere, in her parents' garage with just $500. Now a billionaire, she advises aspiring entrepreneurs: "If you have an idea that solves a real problem, pursue it." Her journey inspires countless business owners worldwide.

**PASSAGE B**

TechSphere develops educational software that serves 50 million students worldwide. The flagship product, which was launched five years ago, uses AI to personalize learning. Students who use the platform improve their test scores by an average of 25%.

**PASSAGE C**

The path wasn't easy. "We almost failed three times," Chen admits. The turning point came when a major investor, who believed in their vision, provided crucial funding. "Unless you find people who share your passion, it's hard to succeed alone."

**PASSAGE D**

At TechSphere, employees who have creative ideas must share them with the team. The best ideas are voted on monthly and get funded. The company's turnover rate is just 5%—far below the industry average of 20%.`,
      
      questions: [
        {
          type: "mc",
          q: "PASSAGE A: How much money did Lisa Chen start with?",
          options: [
            "$500",
            "$50 million",
            "$25"
          ],
          a: "$500",
          successtip: "✓ STARTUP CAPITAL! Initial investment! 💰"
        },
        {
          type: "mc",
          q: "PASSAGE A: Where did she start the company?",
          options: [
            "In her parents' garage",
            "In a billion-dollar office",
            "Worldwide"
          ],
          a: "In her parents' garage",
          successtip: "✓ HUMBLE BEGINNINGS! Starting location! 🏠"
        },
        {
          type: "mc",
          q: "PASSAGE A: 'If you have an idea that solves a real problem' is:",
          options: [
            "First conditional (real advice)",
            "Second conditional (hypothetical)",
            "Zero conditional (scientific fact)"
          ],
          a: "First conditional (real advice)",
          successtip: "✓ ADVICE! Practical guidance! 💡"
        },
        {
          type: "mc",
          q: "PASSAGE B: How many students does TechSphere serve?",
          options: [
            "50 million",
            "25%",
            "Five million"
          ],
          a: "50 million",
          successtip: "✓ USER BASE! Global reach! 🌍"
        },
        {
          type: "mc",
          q: "PASSAGE B: By how much do test scores improve on average?",
          options: [
            "25%",
            "50%",
            "5%"
          ],
          a: "25%",
          successtip: "✓ IMPACT! Performance increase! 📊"
        },
        {
          type: "mc",
          q: "PASSAGE B: The clause 'which was launched five years ago' is:",
          options: [
            "Non-defining (extra timing information)",
            "Defining (identifies which product)",
            "Neither"
          ],
          a: "Non-defining (extra timing information)",
          successtip: "✓ NON-DEFINING! Bonus detail! 📅"
        },
        {
          type: "mc",
          q: "PASSAGE C: How many times did the company almost fail?",
          options: [
            "Three times",
            "Five times",
            "Never"
          ],
          a: "Three times",
          successtip: "✓ CHALLENGES! Struggle count! 💪"
        },
        {
          type: "mc",
          q: "PASSAGE C: What did the major investor provide?",
          options: [
            "Crucial funding",
            "Ideas",
            "Students"
          ],
          a: "Crucial funding",
          successtip: "✓ INVESTMENT! Financial support! 💵"
        },
        {
          type: "mc",
          q: "PASSAGE C: 'Unless you find people who share your passion' means:",
          options: [
            "If you don't find people who share your passion",
            "When you find passionate people",
            "Because you find people"
          ],
          a: "If you don't find people who share your passion",
          successtip: "✓ UNLESS! Conditional conversion! 🎯"
        },
        {
          type: "mc",
          q: "PASSAGE D: What must employees do if they have creative ideas?",
          options: [
            "Share them with the team",
            "Keep them secret",
            "Quit"
          ],
          a: "Share them with the team",
          tip: "Must = obligation!",
          successtip: "✓ POLICY! Company requirement! 📋"
        },
        {
          type: "mc",
          q: "PASSAGE D: What is TechSphere's turnover rate?",
          options: [
            "5%",
            "20%",
            "50%"
          ],
          a: "5%",
          successtip: "✓ RETENTION! Company rate! 📈"
        },
        {
          type: "mc",
          q: "PASSAGE D: What is the industry average turnover rate?",
          options: [
            "20%",
            "5%",
            "Monthly"
          ],
          a: "20%",
          successtip: "✓ BENCHMARK! Industry standard! 📊"
        }
      ]
    },

    {
      id: "m9-rp-s5",
      title: "Set 5: Language & Cultural Understanding 🌏",
      context: `**PASSAGE A**

Marco Giuliani grew up speaking only Italian but now speaks seven languages fluently. His passion, which began at age twelve, has taken him around the world. He works as a translator for the United Nations and believes language learning should start early.

**PASSAGE B**

Research shows that bilingual children, who can switch between languages easily, perform better on problem-solving tasks. "When children learn languages young, they develop better cognitive skills," Marco explains. This scientific fact has been proven in numerous studies worldwide.

**PASSAGE C**

Marco founded LinguaConnect, which teaches languages to underprivileged children. The program operates in 30 countries and relies on volunteers. "Unless we provide free language education, many kids won't have access to these opportunities," he says.

**PASSAGE D**

His advice for language learners is encouraging: "You don't have to be perfect. If you make mistakes, native speakers appreciate the effort." His favorite method is watching movies daily in the target language. "If you do this consistently, you'll improve rapidly."`,
      
      questions: [
        {
          type: "mc",
          q: "PASSAGE A: How many languages does Marco speak fluently?",
          options: [
            "Seven",
            "Twelve",
            "Thirty"
          ],
          a: "Seven",
          successtip: "✓ LANGUAGE COUNT! Total languages! 🗣️"
        },
        {
          type: "mc",
          q: "PASSAGE A: How old was Marco when his passion began?",
          options: [
            "Twelve",
            "Seven",
            "Thirty"
          ],
          a: "Twelve",
          successtip: "✓ STARTING AGE! When interest began! 📅"
        },
        {
          type: "mc",
          q: "PASSAGE A: Where does Marco work?",
          options: [
            "United Nations",
            "LinguaConnect",
            "In 30 countries"
          ],
          a: "United Nations",
          tip: "His job, not his organization!",
          successtip: "✓ EMPLOYER! Professional position! 💼"
        },
        {
          type: "mc",
          q: "PASSAGE B: The clause 'who can switch between languages easily' is:",
          options: [
            "Non-defining (extra description)",
            "Defining (which children)",
            "A question tag"
          ],
          a: "Non-defining (extra description)",
          successtip: "✓ NON-DEFINING! Bonus characteristic! 📚"
        },
        {
          type: "mc",
          q: "PASSAGE B: 'When children learn languages young, they develop better cognitive skills' is:",
          options: [
            "Zero conditional (research finding)",
            "First conditional (future prediction)",
            "Second conditional (hypothetical)"
          ],
          a: "Zero conditional (research finding)",
          successtip: "✓ SCIENTIFIC TRUTH! Research pattern! 🔬"
        },
        {
          type: "mc",
          q: "PASSAGE B: Bilingual children perform better on what tasks?",
          options: [
            "Problem-solving tasks",
            "Language tasks only",
            "Physical tasks"
          ],
          a: "Problem-solving tasks",
          successtip: "✓ COGNITIVE BENEFIT! Mental advantage! 🧠"
        },
        {
          type: "mc",
          q: "PASSAGE C: In how many countries does LinguaConnect operate?",
          options: [
            "30",
            "Seven",
            "Twelve"
          ],
          a: "30",
          successtip: "✓ GLOBAL REACH! Program coverage! 🌍"
        },
        {
          type: "mc",
          q: "PASSAGE C: Who does LinguaConnect rely on?",
          options: [
            "Volunteers",
            "Governments",
            "United Nations"
          ],
          a: "Volunteers",
          successtip: "✓ STAFFING! Program support! 🤝"
        },
        {
          type: "mc",
          q: "PASSAGE C: 'Unless we provide free language education' means:",
          options: [
            "If we don't provide free language education",
            "When we provide education",
            "Because we provide education"
          ],
          a: "If we don't provide free language education",
          successtip: "✓ UNLESS MASTERY! Perfect conversion! ✅"
        },
        {
          type: "mc",
          q: "PASSAGE D: What happens if you make mistakes according to Marco?",
          options: [
            "Native speakers appreciate the effort",
            "You must give up",
            "You can't learn"
          ],
          a: "Native speakers appreciate the effort",
          successtip: "✓ ENCOURAGEMENT! Positive mindset! 😊"
        },
        {
          type: "mc",
          q: "PASSAGE D: What is Marco's favorite learning method?",
          options: [
            "Watching movies daily in the target language",
            "Only reading textbooks",
            "Never practicing"
          ],
          a: "Watching movies daily in the target language",
          successtip: "✓ METHOD! Recommended approach! 🎬"
        },
        {
          type: "mc",
          q: "PASSAGE D: 'If you do this consistently, you'll improve rapidly' is:",
          options: [
            "First conditional (real advice)",
            "Second conditional (impossible)",
            "Zero conditional (scientific fact)"
          ],
          a: "First conditional (real advice)",
          successtip: "✓ PRACTICAL ADVICE! Learning tip! 📚"
        }
      ]
    }
  ]
};
